import { useState } from 'react'
import { motion } from 'framer-motion'
import { Send } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

interface FormState {
  fullname: string
  email: string
  phone: string
  audience: 'privato' | 'professionista' | ''
  topic: string
  message: string
  privacy: boolean
}

const TOPICS = [
  'Informazioni su prodotti',
  'Lavora con noi',
  'Diventa nostro partner',
  'Regali aziendali',
  'Welcome Box',
  'Altro',
] as const

export function ContactForm() {
  const [form, setForm] = useState<FormState>({
    fullname: '',
    email: '',
    phone: '',
    audience: '',
    topic: '',
    message: '',
    privacy: false,
  })
  const [submitted, setSubmitted] = useState(false)

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((s) => ({ ...s, [key]: value }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !form.fullname.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.audience ||
      !form.topic ||
      !form.message.trim() ||
      !form.privacy
    ) {
      return
    }
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] }}
        className="bg-cream-soft px-8 py-14 md:px-14 md:py-20 text-center"
      >
        <span className="font-display italic text-argan-deep text-sm tracking-[0.32em] uppercase">
          Grazie
        </span>
        <h3
          className="mt-6 font-display text-bruno"
          style={{
            fontSize: 'clamp(2rem, 3.5vw, 3rem)',
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
          }}
        >
          <span className="block">Il tuo messaggio</span>
          <span className="block italic font-light text-argan-deep">è stato inviato.</span>
        </h3>
        <p className="mt-6 max-w-md mx-auto text-bruno-soft leading-relaxed">
          Ti risponderemo nel più breve tempo possibile. Nel frattempo, scopri il mondo
          Nashi Argan e i nostri rituali di bellezza.
        </p>
      </motion.div>
    )
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9 }}
      className="space-y-8"
      noValidate
    >
      {/* Row: Name + Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Nome & Cognome" required htmlFor="cf-fullname">
          <input
            id="cf-fullname"
            type="text"
            required
            value={form.fullname}
            onChange={(e) => update('fullname', e.target.value)}
            placeholder="Nome & Cognome"
            className="nashi-input"
            autoComplete="name"
          />
        </Field>

        <Field label="Email" required htmlFor="cf-email">
          <input
            id="cf-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="Email"
            className="nashi-input"
            autoComplete="email"
          />
        </Field>
      </div>

      {/* Row: Phone */}
      <Field label="Numero di telefono" required htmlFor="cf-phone">
        <input
          id="cf-phone"
          type="tel"
          required
          value={form.phone}
          onChange={(e) => update('phone', e.target.value)}
          placeholder="Numero di telefono"
          className="nashi-input"
          autoComplete="tel"
        />
      </Field>

      {/* Audience radio */}
      <fieldset>
        <legend className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-4">
          Sei <span className="text-argan-deep">*</span>
        </legend>
        <div className="flex flex-wrap gap-x-10 gap-y-3">
          {(['privato', 'professionista'] as const).map((value) => (
            <label
              key={value}
              className="group inline-flex items-center gap-3 cursor-pointer"
            >
              <input
                type="radio"
                name="audience"
                value={value}
                required
                checked={form.audience === value}
                onChange={() => update('audience', value)}
                className="sr-only peer"
              />
              <span
                aria-hidden="true"
                className={`relative inline-flex items-center justify-center w-4 h-4 rounded-full border transition-all ${
                  form.audience === value
                    ? 'border-bruno bg-bruno'
                    : 'border-bruno/40 bg-transparent group-hover:border-bruno'
                }`}
              >
                {form.audience === value && (
                  <span className="block w-1.5 h-1.5 rounded-full bg-cream" />
                )}
              </span>
              <span
                className={`font-display italic text-base md:text-lg transition-colors ${
                  form.audience === value ? 'text-bruno' : 'text-bruno-soft group-hover:text-bruno'
                }`}
              >
                {value === 'privato' ? 'Privato' : 'Professionista'}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Topic select */}
      <Field label="Argomento" required htmlFor="cf-topic">
        <div className="relative">
          <select
            id="cf-topic"
            required
            value={form.topic}
            onChange={(e) => update('topic', e.target.value)}
            className="nashi-input appearance-none pr-10 cursor-pointer"
          >
            <option value="" disabled>
              Argomento
            </option>
            {TOPICS.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-bruno-muted"
          >
            ↓
          </span>
        </div>
      </Field>

      {/* Message */}
      <Field label="Messaggio" required htmlFor="cf-message">
        <textarea
          id="cf-message"
          required
          rows={6}
          value={form.message}
          onChange={(e) => update('message', e.target.value)}
          placeholder="Messaggio"
          className="nashi-input resize-y min-h-[140px]"
        />
      </Field>

      {/* GDPR */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          required
          checked={form.privacy}
          onChange={(e) => update('privacy', e.target.checked)}
          className="mt-1 accent-bruno w-3.5 h-3.5 shrink-0"
        />
        <span className="text-[12px] leading-relaxed text-bruno-soft">
          Letta l'informativa sulla{' '}
          <LocalizedLink
            to="privacy"
            className="underline underline-offset-2 hover:text-argan-deep transition-colors"
          >
            Privacy Policy
          </LocalizedLink>
          , autorizzo il trattamento dei miei dati personali. Acconsento al trattamento dei
          miei dati personali per ricevere newsletter e comunicazioni promozionali da
          Nashi Argan. <span className="text-argan-deep">*</span>
        </span>
      </label>

      <div className="pt-4">
        <button
          type="submit"
          className="group inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors"
        >
          Invia
          <Send
            className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5"
            strokeWidth={1.75}
          />
        </button>
      </div>

      <p className="text-[10px] uppercase tracking-[0.28em] text-bruno-muted">
        I campi con <span className="text-argan-deep">*</span> sono obbligatori
      </p>
    </motion.form>
  )
}

function Field({
  label,
  htmlFor,
  required,
  children,
}: {
  label: string
  htmlFor: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3"
      >
        {label}
        {required && <span className="text-argan-deep ml-1">*</span>}
      </label>
      {children}
    </div>
  )
}
